import { Badge, Center, Group, Loader, Tabs, Text } from '@mantine/core';
import { i18nKeys } from 'locales';
import { useTranslation } from 'locales/logic';
import { useParams } from 'react-router';
import { hasAtLeast, isNonNullish, isNullish } from 'remeda';
import { useLoadTasks } from 'shared/hooks/use-load-tasks';

import { TaskListPanel } from './components/TaskListPanel';

export const TaskTab = () => {
  const { t } = useTranslation();

  const { id: debtorId } = useParams<{ id: string }>();

  const { tasks, isTasksLoading } = useLoadTasks(
    { debtor_id: debtorId },
    { enabled: isNonNullish(debtorId) },
  );

  if (isTasksLoading)
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );

  if (isNullish(tasks?.data)) return null;

  const todo = tasks.data.filter(t => t.status_key === 'to_do');
  const completed = tasks.data.filter(t => t.status_key === 'done');
  const skipped = tasks.data.filter(t => t.status_key === 'skipped');

  return (
    <Tabs
      styles={{ list: { borderBottom: '0px dotted var(--mantine-color-purple-1)' } }}
      mt="lg"
      defaultValue="todo"
    >
      <Tabs.List>
        <Tabs.Tab value="todo">
          <Group gap={6}>
            <Text>{t(i18nKeys.TASK.STATUS.PENDING)}</Text>
            {hasAtLeast(todo, 1) && (
              <Badge p="xs" bdrs={6}>
                <Text size="xs" fw={700}>
                  {todo.length}
                </Text>
              </Badge>
            )}
          </Group>
        </Tabs.Tab>
        <Tabs.Tab value="completed">
          <Group gap={6}>
            <Text>{t(i18nKeys.TASK.STATUS.DONE)}</Text>
            {hasAtLeast(completed, 1) && (
              <Badge p="xs" variant="light" bdrs={6}>
                <Text size="xs" fw={700}>
                  {completed.length}
                </Text>
              </Badge>
            )}
          </Group>
        </Tabs.Tab>
        {hasAtLeast(skipped, 1) && (
          <Tabs.Tab value="ignored">
            <Group gap={6}>
              <Text>{t(i18nKeys.TASK.STATUS.SKIPPED)}</Text>
              <Badge p="xs" variant="light" bdrs={6}>
                <Text size="xs" fw={700}>
                  {skipped.length}
                </Text>
              </Badge>
            </Group>
          </Tabs.Tab>
        )}
      </Tabs.List>

      <Tabs.Panel value="todo" pt="md">
        <TaskListPanel tasks={todo} variant="todo" />
      </Tabs.Panel>
      <Tabs.Panel value="completed" pt="md">
        <TaskListPanel tasks={completed} variant="completed" />
      </Tabs.Panel>
      {hasAtLeast(skipped, 1) && (
        <Tabs.Panel value="ignored" pt="md">
          <TaskListPanel tasks={skipped} variant="ignored" />
        </Tabs.Panel>
      )}
    </Tabs>
  );
};
